import React, { useContext, useEffect, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { FormGroup, Slide, Typography } from '@mui/material'
import { Toaster } from 'react-hot-toast'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { decodeToken } from 'react-jwt'
import { PlaybackCountryCodeMap } from '@playback-rewards/shared-libraries'
import { captureEvent } from '@sentry/react'

import Select from '../../components/select'
import { LOCAL_STORAGE, ANALYTICS_EVENT } from '../../constants'
import AuthLayout from '../../components/layout/Auth'
import BottomButton from '../../components/common/BottomButton'
import LoaderContext from '../../context/LoaderContext'
import AuthContext from '../../context/AuthContext'
import { updateUserCountry } from '../../services/authService'
import { handleError, sendGA4Event } from '../../utils/utils'

import './index.css'

const SelectCountry = () => {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const { isLoading, showLoader, hideLoader } = useContext(LoaderContext)
  const { updateUserData } = useContext(AuthContext)

  const [user, setUser] = useState()

  const accessToken = localStorage.getItem(LOCAL_STORAGE.JWT_TOKEN)
    ? localStorage.getItem(LOCAL_STORAGE.JWT_TOKEN)
    : searchParams.get('accessToken')

  const { control, handleSubmit } = useForm({ defaultValues: { country: 'United States' } })

  const setData = async () => setUser(decodeToken(accessToken))

  useEffect(() => {
    showLoader()
    setData().catch(handleError).finally(hideLoader)
  }, [])

  const onSubmit = async (values) => {
    try {
      showLoader()
      const userData = await updateUserCountry({ country: values.country, userId: user.id })

      // Emit Sentry event for audit trail
      captureEvent({
        message: `Manual country selection`,
        level: 'info',
        extra: {
          userId: user?.id,
          selectedCountry: values.country,
        },
      })

      // Emit GA4 event for analytics
      sendGA4Event(ANALYTICS_EVENT.MANUAL_COUNTRY_SELECTION, {
        selected_country: values.country,
        user_id: user?.id,
      })

      // Fetch the updated user data
      updateUserData(userData)
      navigate('/games')
    } catch (error) {
      handleError(error)
    } finally {
      hideLoader()
    }
  }

  return (
    <>
      <Toaster />
      <Slide direction="left" in={true} mountOnEnter unmountOnExit>
        <div className={isLoading ? 'blured' : ''}>
          <AuthLayout
            title="Select your country"
            content={
              <div className="eligibilityGateContent">
                <Typography variant="body1">
                  We are unable to detect your country. Please choose your country from the
                  dropdown.
                </Typography>
                <form className="eligibilityGateForm" onSubmit={handleSubmit(onSubmit)}>
                  <FormGroup>
                    <Controller
                      control={control}
                      name="country"
                      rules={{
                        required: {
                          value: true,
                        },
                      }}
                      render={({ field: { onChange, value } }) => (
                        <Select
                          currentItem={value}
                          options={Object.values(PlaybackCountryCodeMap)}
                          onChange={onChange}
                        />
                      )}
                    />
                  </FormGroup>
                </form>
                <div className="noteText">
                  <Typography variant="body2">
                    <strong>NOTE:</strong> To redeem gift cards you will need to verify your
                    identity and choosing the wrong country is a violation of Terms of Service.
                  </Typography>
                </div>
              </div>
            }
          >
            <BottomButton handleClick={handleSubmit(onSubmit)} style={{ marginBottom: '20px' }}>
              Continue
            </BottomButton>
          </AuthLayout>
        </div>
      </Slide>
    </>
  )
}

export default SelectCountry
